import { useMemo } from 'react';
import { find } from 'lodash';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import {
  List, ListItem, ListItemAvatar, ListItemText,
  Stack, FilledInput,
} from '@mui/material';
import { algoliaClient } from '@client/algolia';
import { AlgoliaIndices } from '@sda/enums/algolia';
import { getAlgoliaIndexName, serializeFilters } from '@sda/utils/algolia';
import { getSelectedProfileId } from '@client/selectors/helpers/profile';
import { SearchBox } from '@client/algolia/InstantSearch/SearchBox';
import { InfiniteHits, LoadInfiniteHits } from '@client/algolia/InstantSearch/InfiniteHits';
import { Avatar } from '@client/components/common/Avatar';
import ActPlaceholderImage from '@client/images/placeholders/artist-small.jpg';
import { UserAvatar } from '@client/components/common/Avatar/UserAvatar';
import { getUserId } from '@client/selectors/helpers/login';
import { useSelector } from '../redux.hooks';
import type { Connection, ConnectionSearchOptions } from './interfaces';
import type { AlgoliaConnection } from '@sda/helpers/algolia.helpers';
import type { AlgoliaFilter } from '@sda/utils/algolia/interfaces';

const defaultFilters = { };

const attributesToRetrieve = [
  'publicProfiles',
  'publicThread',
];

interface SearchComponentProps extends ConnectionSearchOptions {
  readonly connectionId: string;
  readonly setConnectionId: (data: Connection) => void;
}

export const SearchComponent: React.FC<SearchComponentProps> = ({ connectionId, setConnectionId, includeProfiles }) => {
  const selectedProfileId = useSelector(getSelectedProfileId);
  const userId = useSelector(getUserId);

  const filters = useMemo(() => {
    if (includeProfiles) {
      return {
        ...defaultFilters,
        'publicThread.profileHash': {
          type: 'string',
          value: selectedProfileId,
          not: true,
        },
        connection: {
          type: 'filterGroup',
          group: {
            'publicThread.profileIds': {
              type: 'string',
              value: selectedProfileId,
              or: true,
            },
            'publicThread.collaborators': {
              type: 'string',
              value: userId,
              or: true,
            },
          },
        },
      };
    }

    return {
      ...defaultFilters,
      'publicThread.profileIds': {
        type: 'string',
        value: selectedProfileId,
      },
    };
  }, [userId, selectedProfileId, includeProfiles]);

  return (
    <InstantSearch
      searchClient={algoliaClient}
      indexName={getAlgoliaIndexName(AlgoliaIndices.CONNECTIONS)}
    >
      <Configure
        filters={serializeFilters(filters as Record<string, AlgoliaFilter>)}
        hitsPerPage={10}
        attributesToRetrieve={attributesToRetrieve}
        attributesToHighlight={[]}
        distinct
      />

      <Stack spacing={1.5}>
        <SearchBox
          margin="dense"
          component={FilledInput}
          placeholder="Search Connections"
          fullWidth
        />

        <InfiniteHits<any, AlgoliaConnection>
          component={List}
          dense
          sx={{
            maxHeight: 170,
            overflowY: 'auto',
          }}
          hitComponent={({ hit }) => {
            const profile = find(hit.publicProfiles, (profileData) => profileData.id !== selectedProfileId);

            if (!profile) {
              return null;
            }

            return (
              <ListItem
                aria-label={profile.name}
                onClick={() => setConnectionId({ connectionId: hit.objectID, connectionType: hit.publicThread.documentType })}
                disablePadding
                button
              >
                <ListItemAvatar>
                  {profile.fullName ? (
                    <UserAvatar
                      displayName={profile.fullName}
                      emailHash={null}
                      images={null}
                      size="small"
                    />
                  ) : (
                    <Avatar
                      alt={profile.name}
                      images={profile.publicImageUrl}
                      fallback={ActPlaceholderImage}
                      size="small"
                    />
                  )}
                </ListItemAvatar>

                <ListItemText
                  primary={profile.name || profile.fullName}
                  primaryTypographyProps={{
                    variant: hit.objectID === connectionId ? 'subtitle2' : 'subtitle1',
                    color: hit.objectID === connectionId ? 'success' : 'textSecondary',
                  }}
                />
              </ListItem>
            );
          }}
        />

        <LoadInfiniteHits align="left" />
      </Stack>
    </InstantSearch>
  );
};
